import { EditDialog } from 'components/EditDialog/EditDialog';
import { NumericTextField } from 'components/FormFields/NumericTextField';
import { Control, Controller, useForm, UseFormSetValue } from 'react-hook-form';
import { useContext, useEffect, useState, ReactElement, SyntheticEvent } from 'react';
import { useSnackbar } from 'notistack';
import { Trans, useTranslation } from 'react-i18next';
import { useAppSelector } from '../../redux/hooks';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useGetManufacturerFileQuery } from '../../redux/services/projects';
import {
  Division,
  FastenerMaterial,
  Fasteners,
  Gauge,
  ManufacturerFile,
  ScrewDiameter,
  SupportMaterial,
  WasherType,
} from 'types/domainTypes';
import {
  Alert,
  Box,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
  TextField,
} from '@mui/material';
import { CalculationContext } from './CalculationContextProvider';
import { FastenerRecommendation } from './FastenerRecommendation';
import { AppBaseButton } from '../../components/AppBaseButton/AppBaseButton';
import PullOutDesignResistance from '../../assets/images/PullOutDesignResistance.svg';
import PullOverDesignResistance from '../../assets/images/PullOverDesignResistance.svg';
import SteelGauge from '../../assets/images/SteelGauge.svg';

export type FastenersDetailsFormData = {
  pullOutDesignResistance?: number;
  pullOverDesignResistance?: number;
  // Recommended Fastener Fields
  manufacturerName?: string;
  supportMaterial?: string;
  gauge?: string;
  washerType?: string;
  fastenerMaterial?: string;
  screwDiameter?: string;
  fastenerName?: string;
  testStandard?: string;
  notes?: string;
};

const fastenersDetailsFormDefaults: FastenersDetailsFormData = {
  pullOutDesignResistance: undefined,
  pullOverDesignResistance: undefined,
  // Recommended Fastener Fields
  manufacturerName: '',
  fastenerName: '',
  supportMaterial: '',
  gauge: '',
  washerType: '',
  fastenerMaterial: '',
  screwDiameter: '',
  testStandard: '',
  notes: '',
};

export const KS_AMBER_WARNING_COLOR = '653D00';

export const alertMsgTitleStyles = {
  color: KS_AMBER_WARNING_COLOR,
  fontWeight: 'bold',
};

export const alertMsgBodyStyles = {
  color: KS_AMBER_WARNING_COLOR,
  fontWeight: 'normal',
  fontSize: '14px',
};

export type RecommendedFastenerFormData = {
  id: string;
  manufacturerName: string;
  supportMaterial: string;
  gauge: string;
  washerType: string;
  fastenerMaterial: string;
  screwDiameter: string;
};

export const defaultRecommendedFastenerFormData: RecommendedFastenerFormData = {
  id: '',
  manufacturerName: '',
  supportMaterial: '',
  gauge: '',
  washerType: '',
  fastenerMaterial: '',
  screwDiameter: '',
};

export type FastenerOptions = {
  id: string;
  manufacturerName: string;
  supportMaterial: string;
  gauge: string;
  washerType: string;
  fastenerMaterial: string;
  screwDiameter: string;
};

export type FastenerOptionsProps = {
  name: keyof FastenerOptions;
  label: string;
  options: string[] | ManufacturerFile[] | SupportMaterial[] | Gauge[] | WasherType[] | FastenerMaterial[] | ScrewDiameter[];
  control: Control<RecommendedFastenerFormData>;
  onClick: (fieldName: keyof FastenerOptions, value: string) => void;
  onClickLastOption?: (lastOption: ScrewDiameter) => void;
  isLoading?: boolean;
  setValue: UseFormSetValue<RecommendedFastenerFormData>;
  disabled: boolean;
  visible?: boolean;
};

interface FastenersDetailsDialogProps {
  onClose: () => void;
  onSave: (fastenersDetails: Fasteners) => Promise<void>;
  fasteners?: Fasteners;
  fastenerCheck?: boolean;
  productCheck: boolean;
  manufacturers?: ManufacturerFile[];
}

type RadioOptions = {
  value: string;
  label: string;
};

type FastenerBoundary = {
  value: number;
  errorText: string;
};

type FastenerInputBoundaries = {
  min: FastenerBoundary;
  max: FastenerBoundary;
};

type EndAdornmentProps = {
  dataTestId: string;
  imgSrc: string;
  imgAlt: string;
  text: string;
};

const fieldNames: (keyof FastenerOptions)[] = [
  'manufacturerName',
  'supportMaterial',
  'gauge',
  'washerType',
  'fastenerMaterial',
  'screwDiameter',
];

export function getInitialRadioSelection(productCheck: boolean, fasteners: Fasteners | undefined): string {
  if (!productCheck && !fasteners) {
    return 'manual';
  }
  if (productCheck && !fasteners) {
    return 'recommended';
  }
  if (fasteners?.fastenerName && fasteners.manufacturerName) {
    return 'recommended';
  }
  if (fasteners?.pullOutDesignResistance && fasteners?.pullOverDesignResistance) {
    return 'manual';
  } else {
    return '';
  }
}

function parseFasteners(fasteners?: Fasteners): FastenersDetailsFormData {
  if (fasteners === undefined) {
    return fastenersDetailsFormDefaults;
  }
  return {
    pullOutDesignResistance: fasteners.pullOutDesignResistance,
    pullOverDesignResistance: fasteners.pullOverDesignResistance,
    manufacturerName: fasteners.manufacturerName || '',
    fastenerName: fasteners.fastenerName || '',
  };
}

interface RecommendedFastenerFormProps {
  fasteners?: Fasteners;
  isLoading?: boolean;
  setIsDirty: (value: boolean) => void;
  fastenersOptions?: ManufacturerFile[];
  setFastenersData: (fasteners: Fasteners) => void;
  hasValidRecommendation: (value: boolean) => void;
  hasValidManufacturer: (value: boolean) => void;
  clearAllTrigger: boolean;
  setClearAllTrigger: (value: boolean) => void;
}

export function handleAdornment(
  name: 'id' | 'manufacturerName' | 'supportMaterial' | 'gauge' | 'washerType' | 'fastenerMaterial' | 'screwDiameter'
): { endAdornment: JSX.Element } | undefined {
  const adornment =
    name === 'gauge'
      ? {
          endAdornment: (
            <InputAdornment
              position="end"
              disablePointerEvents={true}
              style={{
                position: 'absolute',
                paddingLeft: '86%',
                paddingRight: '14%',
              }}
            >
              <img alt="steel-gauge-icon" src={SteelGauge} />
            </InputAdornment>
          ),
        }
      : undefined;
  return adornment;
}

export function FastenerOption({
  name,
  label,
  options,
  control,
  onClick,
  onClickLastOption,
  isLoading,
  // setValue,
  disabled,
  visible = true,
}: FastenerOptionsProps): ReactElement {
  const { t } = useTranslation();
  const requiredHelperText = `*${t('common.required-helper-text')}`;

  if (isLoading) {
    return (
      <Skeleton
        key={name}
        sx={{
          height: '59px',
          transform: 'none',
          marginBottom: '30px !important',
        }}
      />
    );
  }

  if (options.length === 0) {
    return (
      <Alert
        severity="error"
        sx={{
          backgroundColor: '#fdeded',
          '& .MuiAlert-icon': {
            color: '#d32f2f',
          },
        }}
        data-testid="fasteners-details-dialog-no-data"
      >
        <Trans i18nKey="calculation-page.fasteners-details-dialog.error-message-no-options-data">{{ option: label }}</Trans>
      </Alert>
    );
  }

  const baseStyle = () => ({
    pb: 0,
    pr: 0,
    mb: 0,
    display: visible ? undefined : 'none',
    '& .MuiSelect-select.MuiInputBase-input.MuiOutlinedInput-input.Mui-disabled ': {
      WebkitTextFillColor: 'rgb(0, 0, 0)',
    },
    '& .MuiInputLabel-root.MuiInputLabel-formControl.MuiInputLabel-animated.MuiInputLabel-shrink.MuiInputLabel-outlined.MuiFormLabel-root.MuiFormLabel-colorPrimary.Mui-disabled.MuiFormLabel-filled':
      {
        color: 'rgba(0, 0, 0, 0.6)',
      },
    '& .MuiFormHelperText-root.Mui-disabled.MuiFormHelperText-sizeMedium.MuiFormHelperText-contained.MuiFormHelperText-filled': {
      color: 'rgba(0, 0, 0, 0.6)',
    },
    '& .MuiInputBase-adornedEnd': {
      paddingRight: 0,
    },
  });

  const adornment = handleAdornment(name);

  return (
    <Controller
      name={name}
      control={control}
      rules={{ required: true }}
      render={({ field, fieldState }) => {
        return (
          <TextField
            select
            error={fieldState.invalid}
            onChange={field.onChange}
            value={field.value}
            helperText={requiredHelperText}
            label={label}
            sx={baseStyle()}
            disabled={disabled}
            InputProps={{
              ...adornment,
            }}
            fullWidth
          >
            {options.map((option, index) => (
              <MenuItem
                key={index}
                value={typeof option === 'string' ? option : option.value}
                onClick={() => {
                  if (onClickLastOption) {
                    onClickLastOption(option as ScrewDiameter);
                  }

                  onClick(field.name, typeof option === 'string' ? option : option.value);
                }}
              >
                {typeof option === 'string' ? option : option.value}
              </MenuItem>
            ))}
          </TextField>
        );
      }}
    />
  );
}

export function RecommendedFastenerForm({
  fasteners,
  isLoading,
  setIsDirty,
  fastenersOptions,
  setFastenersData,
  hasValidRecommendation,
  hasValidManufacturer,
  clearAllTrigger,
  setClearAllTrigger,
}: RecommendedFastenerFormProps): JSX.Element {
  const { t } = useTranslation();
  const {
    control,
    watch,
    reset,
    setValue,
    clearErrors,
    formState: { isDirty },
  } = useForm<RecommendedFastenerFormData>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: fasteners || defaultRecommendedFastenerFormData,
  });
  const [fastenersRecommendation, setFastenersRecommendation] = useState<Fasteners>();
  useEffect(() => {
    if (fasteners) {
      setFastenersRecommendation(fasteners);
      setFastenersData(fasteners);
    }
  }, [fasteners, setFastenersRecommendation, setFastenersData]);
  useEffect(() => {
    setIsDirty(isDirty);
  }, [isDirty, setIsDirty]);

  const selected = {
    manufacturerName: watch('manufacturerName'),
    supportMaterial: watch('supportMaterial'),
    gauge: watch('gauge'),
    washerType: watch('washerType'),
    fastenerMaterial: watch('fastenerMaterial'),
    screwDiameter: watch('screwDiameter'),
  };

  const handleClickLastOption = (lastOption: ScrewDiameter) => {
    const { value, fastenerName, testStandard, notes } = lastOption;
    const selectedFastener: Fasteners = {
      fastenerName,
      manufacturerName: selected.manufacturerName,
      supportMaterial: selected.supportMaterial,
      gauge: String(selected.gauge),
      washerType: selected.washerType,
      fastenerMaterial: selected.fastenerMaterial,
      screwDiameter: value,
      pullOutDesignResistance: Number(lastOption.pullOutDesignResistance),
      pullOverDesignResistance: Number(lastOption.pullOverDesignResistance),
      testStandard,
      notes,
    };
    setFastenersData(selectedFastener);
    setFastenersRecommendation(selectedFastener);
  };

  const setValueIfOnlyOption = (
    fieldName: keyof FastenerOptions,
    availableOptions: ManufacturerFile[] | SupportMaterial[] | Gauge[] | WasherType[] | FastenerMaterial[] | ScrewDiameter[],
    fieldValue: string
  ) => {
    if (availableOptions.length === 1 && typeof availableOptions[0] !== 'string') {
      if (fieldValue !== (availableOptions[0].value as typeof fieldValue)) {
        setValue(fieldName, availableOptions[0].value as typeof fieldValue, { shouldDirty: false });
        if (fieldName === 'screwDiameter') {
          const lastOption = availableOptions[0] as ScrewDiameter;
          handleClickLastOption(lastOption);
        }
      }
    }
  };

  useEffect(() => {
    setValueIfOnlyOption('manufacturerName', fastenersOptions || [], selected.manufacturerName);
  });

  const supportMaterialOptions =
    fastenersOptions?.find((manufacturerName: ManufacturerFile) => manufacturerName.value === selected.manufacturerName)
      ?.supportMaterials || [];
  useEffect(() => {
    setValueIfOnlyOption('supportMaterial', supportMaterialOptions, selected.supportMaterial);
  });

  const numericGaugeOptions =
    supportMaterialOptions?.find((supportMaterial: SupportMaterial) => supportMaterial.value === selected.supportMaterial)?.gauges || [];
  const gaugeOptions: Gauge[] = numericGaugeOptions.map((g) => ({
    ...g,
    value: String(g.value),
  }));
  useEffect(() => {
    setValueIfOnlyOption('gauge', gaugeOptions, selected.gauge);
  });

  const washerTypeOptions = gaugeOptions?.find((gauge: Gauge) => gauge.value === selected.gauge)?.washerTypes || [];
  useEffect(() => {
    setValueIfOnlyOption('washerType', washerTypeOptions, selected.washerType);
  });

  const fastenerMaterialOptions =
    washerTypeOptions?.find((washerType: WasherType) => washerType.value === selected.washerType)?.fastenerMaterials || [];
  useEffect(() => {
    setValueIfOnlyOption('fastenerMaterial', fastenerMaterialOptions, selected.fastenerMaterial);
  });

  const numericScrewDiameterOptions =
    fastenerMaterialOptions?.find((fastenerMaterial: FastenerMaterial) => fastenerMaterial.value === selected.fastenerMaterial)
      ?.screwDiameters || [];
  const screwDiameterOptions: ScrewDiameter[] = numericScrewDiameterOptions.map((s: ScrewDiameter) => ({
    ...s,
    value: String(s.value),
  }));
  useEffect(() => {
    setValueIfOnlyOption('screwDiameter', screwDiameterOptions, selected.screwDiameter);
  });

  const clearSubsequentFields = (fieldName: keyof FastenerOptions) => {
    const idx = fieldNames.findIndex((field) => field === fieldName);
    for (let i = idx + 1; i < fieldNames.length; i++) {
      setValue(fieldNames[i], '');
    }
  };

  const onClick = (fieldName: keyof FastenerOptions, value: string) => {
    clearErrors();
    if (fieldName !== 'id' && selected[fieldName] !== value) {
      clearSubsequentFields(fieldName);
    }
  };

  useEffect(() => {
    if (clearAllTrigger) {
      reset(defaultRecommendedFastenerFormData);
      setClearAllTrigger(false);
    }
  }, [setClearAllTrigger, clearAllTrigger, reset]);

  const showNoMatchesAlertMessage: JSX.Element = (
    <Alert severity="warning">
      <span data-testid="alert-message-title" style={alertMsgTitleStyles}>
        {t('calculation-page.fasteners-details-dialog.no-matches-warning-title')}
      </span>
      <br />
      <span data-testid="alert-message" style={alertMsgBodyStyles}>
        {t('calculation-page.fasteners-details-dialog.no-matches-warning-message')}
      </span>
    </Alert>
  );

  useEffect(() => {
    if (selected.screwDiameter) {
      hasValidRecommendation(true);
    } else {
      hasValidRecommendation(false);
    }
  }, [selected.screwDiameter, hasValidRecommendation]);

  useEffect(() => {
    if (selected.manufacturerName) {
      hasValidManufacturer(true);
    } else {
      hasValidManufacturer(false);
    }
  }, [selected.manufacturerName, hasValidManufacturer, fastenersOptions]);

  return (
    <DialogContent
      sx={{
        padding: 0,
        paddingTop: 1,
        display: 'flex',
        flexDirection: 'column',
        overflowX: 'hidden',
      }}
      data-testid={'recommended-fasteners-form'}
    >
      {fastenersOptions?.length === 0 ? (
        showNoMatchesAlertMessage
      ) : (
        <Stack sx={{ padding: 0 }} spacing={3}>
          <FastenerOption
            name="manufacturerName"
            label={t('calculation-page.fasteners-details-dialog.label-fastener-manufacturer')}
            options={fastenersOptions || []}
            control={control}
            onClick={onClick}
            setValue={setValue}
            disabled={fastenersOptions?.length === 1}
            isLoading={isLoading}
          />
          {selected.manufacturerName && (
            <FastenerOption
              name="supportMaterial"
              label={t('calculation-page.fasteners-details-dialog.label-fastener-support-material')}
              options={supportMaterialOptions || []}
              control={control}
              onClick={onClick}
              setValue={setValue}
              isLoading={isLoading}
              disabled={supportMaterialOptions.length === 1}
            />
          )}
          {selected.supportMaterial && (
            <FastenerOption
              name="gauge"
              label={t('calculation-page.fasteners-details-dialog.label-fastener-gauge')}
              options={gaugeOptions || []}
              control={control}
              onClick={onClick}
              setValue={setValue}
              isLoading={isLoading}
              disabled={gaugeOptions.length === 1}
              visible={selected.supportMaterial !== 'Timber'}
            />
          )}
          {selected.gauge && (
            <FastenerOption
              name="washerType"
              label={t('calculation-page.fasteners-details-dialog.label-fastener-washer-type')}
              options={washerTypeOptions || []}
              control={control}
              onClick={onClick}
              setValue={setValue}
              isLoading={isLoading}
              disabled={washerTypeOptions.length === 1}
            />
          )}
          {selected.washerType && (
            <FastenerOption
              name="fastenerMaterial"
              label={t('calculation-page.fasteners-details-dialog.label-fastener-fastener-material')}
              options={fastenerMaterialOptions || []}
              control={control}
              onClick={onClick}
              setValue={setValue}
              isLoading={isLoading}
              disabled={fastenerMaterialOptions.length === 1}
            />
          )}
          {selected.fastenerMaterial && (
            <FastenerOption
              name="screwDiameter"
              label={t('calculation-page.fasteners-details-dialog.label-fastener-screw-diameter')}
              onClickLastOption={handleClickLastOption}
              options={screwDiameterOptions || []}
              control={control}
              onClick={onClick}
              setValue={setValue}
              isLoading={isLoading}
              disabled={screwDiameterOptions.length === 1}
            />
          )}
        </Stack>
      )}

      {/* Recommended Fastener Component */}
      {selected.screwDiameter && fastenersRecommendation && <FastenerRecommendation fastenersRecommendation={fastenersRecommendation} />}
    </DialogContent>
  );
}

export function FastenersDetailsDialog({ onClose, onSave, fasteners, productCheck }: FastenersDetailsDialogProps): ReactElement {
  const [fastenerRadioSelection, setFastenerRadioSelection] = useState<string>(getInitialRadioSelection(productCheck, fasteners));
  const [hasValidRecommendation, setHasValidRecommendation] = useState<boolean>(false);
  const [, setHasManufacturer] = useState<boolean>(false);
  const [clearAllTrigger, setClearAllTrigger] = useState<boolean>(false);
  const [fastenersData, setFastenersData] = useState<Fasteners>({
    manufacturerName: '',
    supportMaterial: '',
    gauge: '',
    washerType: '',
    fastenerMaterial: '',
    screwDiameter: '',
    fastenerName: '',
    pullOutDesignResistance: 0,
    pullOverDesignResistance: 0,
    testStandard: '',
    notes: '',
  });
  const [, setShouldShowFastenersTooltip] = useState<boolean>(false);
  const [isRecommendedDirty, setIsRecommendedDirty] = useState(false);
  const { t } = useTranslation();
  const {
    control,
    handleSubmit,
    formState: { isDirty, isValid },
  } = useForm<FastenersDetailsFormData>({
    mode: 'onChange',
    defaultValues: parseFasteners(fasteners),
  });

  const { enqueueSnackbar } = useSnackbar();
  const { calculation } = useContext(CalculationContext);
  const division: Division = useAppSelector((state) => state.user.division);
  const applicationType = calculation?.details.application === 'RoofPanel' ? 'Roof' : 'Wall';
  const panelType = calculation?.product?.panelType;
  const externalFaceThickness = calculation?.product?.externalFaceThickness.toString();

  const { data, isLoading, error } = useGetManufacturerFileQuery(
    externalFaceThickness !== undefined
      ? {
          division,
          applicationType,
          panelType,
          externalFaceThickness,
        }
      : skipToken
  );

  useEffect(() => {
    if (error) {
      enqueueSnackbar(t('calculation-page.fasteners-details-dialog.error-loading-manufacturer-file'), {
        variant: 'error',
      });
    }
  }, [t, enqueueSnackbar, error, data]);

  const isFormInEdit = !!fasteners;

  const radioOptions: RadioOptions[] = [
    {
      value: 'manual',
      label: t('calculation-page.fasteners-details-dialog.radio-group-label-manual'),
    },
    {
      value: 'recommended',
      label: t('calculation-page.fasteners-details-dialog.radio-group-label-recommended'),
    },
  ];

  const fastenerInputBoundaries: FastenerInputBoundaries = {
    min: {
      value: 0,
      errorText: t('calculation-page.fasteners-details-dialog.error-min-value-pull-out-pull-over'),
    },
    max: {
      value: 999.999,
      errorText: t('calculation-page.fasteners-details-dialog.error-max-value-pull-out-pull-over'),
    },
  };

  const submitHandler = (formData: FastenersDetailsFormData) => {
    if (fastenerRadioSelection === '') return;
    const isRecommended = fastenerRadioSelection === 'recommended';

    let payloadData: Fasteners = {
      pullOutDesignResistance: isRecommended ? fastenersData.pullOutDesignResistance : formData.pullOutDesignResistance,
      pullOverDesignResistance: isRecommended ? fastenersData.pullOverDesignResistance : formData.pullOverDesignResistance,
    };

    if (isRecommended) {
      payloadData = { ...fastenersData, ...payloadData };

      console.log('payloadData.gauge', payloadData.gauge);
      if (payloadData.gauge === '0') {
        payloadData.gauge = undefined;
      }

      console.log('payloadData.gauge after', payloadData.gauge);
    }

    setShouldShowFastenersTooltip(false);
    onSave(payloadData);
  };

  const handleUpdateHasValidRecommendation = (value: boolean) => {
    setHasValidRecommendation(value);
  };

  const handleUpdateHasValidManufacturer = (value: boolean) => {
    setHasManufacturer(value);
  };

  const handleUpdateIsRecommendedDirty = (value: boolean) => {
    setIsRecommendedDirty(value);
  };

  const handleRadioChange = (e: SyntheticEvent<Element, Event>) => {
    setFastenerRadioSelection((e.target as HTMLInputElement).value);
  };

  // Local Components
  function EndAdornment({ dataTestId, imgSrc, imgAlt, text }: EndAdornmentProps): JSX.Element {
    return (
      <>
        <img data-testid={dataTestId} src={imgSrc} alt={t(imgAlt)} style={{ marginRight: '0.5rem' }} />
        <span style={{ marginTop: '0.35rem' }}>{text}</span>
      </>
    );
  }

  const customButtons: JSX.Element = (
    <>
      <Box
        sx={{
          flex: 1,
          flexDirection: 'row',
          justifyContent: 'flex-start',
        }}
      ></Box>
      <AppBaseButton
        variant="contained"
        disabled={!hasValidRecommendation}
        onClick={() => handleSubmit(submitHandler)()}
        data-testid="fasteners-submit-btn"
        id="fasteners-submit-btn"
      >
        {isFormInEdit ? t('common.edit-dialog.action-save') : t('calculation-page.fasteners-details-dialog.save-action-text')}
      </AppBaseButton>
    </>
  );

  const showAlertMessage: JSX.Element = (
    <Alert severity="info">
      <span data-testid="alert-message-title">{t('calculation-page.fasteners-details-dialog.info-message-title')}</span>
      <br />
      <span data-testid="alert-message" style={{ fontWeight: 'normal', fontSize: '14px' }}>
        {t('calculation-page.fasteners-details-dialog.info-message')}
      </span>
    </Alert>
  );

  const manualFields: JSX.Element = (
    <>
      <NumericTextField
        name="pullOutDesignResistance"
        required
        control={control}
        label={t('calculation-page.fasteners-details-dialog.label-pull-out-design-resistance')}
        fullWidth
        minValue={fastenerInputBoundaries.min}
        maxValue={fastenerInputBoundaries.max}
        adornmentProps={{
          endAdornment: (
            <EndAdornment
              dataTestId={'pull-out-icon'}
              imgSrc={PullOutDesignResistance}
              imgAlt={'calculation-page.fasteners-details-dialog.label-pull-out-design-resistance'}
              text={'kN'}
            />
          ),
        }}
        decimalPlaces={3}
        id="pull-out-design-resistance-manual-entry"
        data-testid="min-design-pull-out"
        style={{ marginTop: '0.25rem' }}
      />
      <NumericTextField
        name="pullOverDesignResistance"
        required
        control={control}
        label={t('calculation-page.fasteners-details-dialog.label-pull-over-design-resistance')}
        fullWidth
        minValue={fastenerInputBoundaries.min}
        maxValue={fastenerInputBoundaries.max}
        adornmentProps={{
          endAdornment: (
            <EndAdornment
              dataTestId={'pull-over-icon'}
              imgSrc={PullOverDesignResistance}
              imgAlt={'calculation-page.fasteners-details-dialog.label-pull-over-design-resistance'}
              text={'kN'}
            />
          ),
        }}
        decimalPlaces={3}
        id="pull-over-design-resistance-manual-entry"
        data-testid="min-design-pull-over"
        style={{ marginTop: '1rem', marginBottom: '0.5rem' }}
      />
    </>
  );

  return (
    <EditDialog
      title={
        isFormInEdit ? t('calculation-page.fasteners-details-dialog.edit-title') : t('calculation-page.fasteners-details-dialog.title')
      }
      isDirty={isDirty || isRecommendedDirty}
      onClose={onClose}
      saveActionDisabled={!isValid} // for manual entry mode only
      saveActionText={isFormInEdit ? t('common.edit-dialog.action-save') : t('calculation-page.fasteners-details-dialog.save-action-text')}
      customButtons={fastenerRadioSelection === 'recommended' ? customButtons : null}
      handleFormSubmit={() => handleSubmit(submitHandler)()}
      saveActionTestId="fasteners-submit-btn"
    >
      {!productCheck && showAlertMessage}
      <FormControl>
        <FormLabel data-testid="radio-group-title" style={{ marginTop: '0.25rem' }}>
          {t('calculation-page.fasteners-details-dialog.sub-title')}
        </FormLabel>
        <RadioGroup
          defaultValue={fastenerRadioSelection}
          name="radio-buttons-group"
          data-testid="radio-group-section"
          style={{ marginTop: '0.25rem' }}
        >
          <FormControlLabel
            value={radioOptions[0].value}
            name={radioOptions[0].value}
            control={<Radio />}
            label={radioOptions[0].label}
            data-testid={`radio-${radioOptions[0].value}`}
            sx={{
              marginBottom: '.25rem',
              '.MuiFormControlLabel-label': {
                marginTop: '.25rem',
              },
            }}
            onChange={(e) => {
              handleRadioChange(e);
            }}
          />

          {/* Manual Fields */}
          {fastenerRadioSelection === 'manual' && manualFields}

          <FormControlLabel
            value={radioOptions[1].value}
            name={radioOptions[1].value}
            control={<Radio />}
            disabled={!productCheck}
            label={radioOptions[1].label}
            data-testid={`radio-${radioOptions[1].value}`}
            onChange={(e) => {
              handleRadioChange(e);
            }}
            sx={{
              marginBottom: '.25rem',
              '.MuiFormControlLabel-label': {
                marginTop: '.25rem',
              },
            }}
          />

          {/* Recommended Fields */}
          {fastenerRadioSelection === 'recommended' && (
            <RecommendedFastenerForm
              fasteners={fasteners}
              isLoading={isLoading}
              fastenersOptions={data}
              clearAllTrigger={clearAllTrigger}
              setFastenersData={setFastenersData}
              setClearAllTrigger={setClearAllTrigger}
              setIsDirty={handleUpdateIsRecommendedDirty}
              hasValidManufacturer={handleUpdateHasValidManufacturer}
              hasValidRecommendation={handleUpdateHasValidRecommendation}
            />
          )}
        </RadioGroup>

        {!fastenerRadioSelection && (
          <FormHelperText data-testid="form-required-label-top" style={{ marginTop: '0.5rem' }}>
            *{t('common.required-helper-text')}
          </FormHelperText>
        )}
      </FormControl>
    </EditDialog>
  );
}
